import React, { useState, useRef } from 'react';
import { DocumentType } from '../../types/dataRoom';
import { useDataRoom } from '../../context/DataRoomContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'react-toastify';
import { Upload, FileText, X, Check, AlertCircle } from 'lucide-react';
import { formatDocumentType } from '../../utils/documentUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { motion } from 'framer-motion';
import apiService from '../../services/apiService';
import { useParams } from "react-router-dom";

interface DocumentUploaderProps {
  allowedTypes?: DocumentType[];
  directorId?: string;
  label?: string;
  description?: string;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({ 
  allowedTypes,
  directorId,
  label = 'Upload Document',
  description
}) => {
  const { addDocument, addDocuments } = useDataRoom();
  const [selectedType, setSelectedType] = useState<DocumentType>(allowedTypes && allowedTypes.length === 1 ? allowedTypes[0] : 'other');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { customerId } = useParams();
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    }
  };
  
  const handleUploadClick = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };
  
  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    }
  };
  

  const getDocumentCategory = (type: DocumentType): string => {
    
    switch(type) {
      case 'audited_financials_2022_23':
      case 'audited_financials_2023_24':
      case 'audited_financials_2024_25':
      case 'provisional_financials':
      case 'mis_financials':
      case 'itr':
        return 'Financials';
      case 'bank_statements':
      case 'gst_filing':
      case 'sanction_letter':
        return 'Banking';
      case 'company_kyc':
      case 'director_kyc':
      case 'company_deck':
        return 'KYC Documents';
      default:
        return 'Other';
    }
  };

  
  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }
  
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });
    formData.append("customerId", customerId || "");
    formData.append("documentType", selectedType || "");
    formData.append("documentCategory", getDocumentCategory(selectedType));
  
    setUploading(true);
    setUploadProgress(0);
  
    try {
      const response: any = await apiService.post(
        "/data-room/multi-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
              // Cap progress at 98% until response comes back
              setUploadProgress(Math.min(percent, 98));
            }
          }
        }
      );
  
      // API response received — now set full 100% before hiding
      setUploadProgress(100);
      addDocuments()
    
  
      toast.success('Upload completed successfully');
  
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error('Upload failed. Please try again.');
    } finally {
      // Add small delay to let user see 100% bar
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  };
  
  
  
  const types = allowedTypes || [
    'audited_financials_2022_23',
    'audited_financials_2023_24',
    'audited_financials_2024_25',
    'bank_statements',
    'company_kyc',
    'director_kyc',
    'gst_filing',
    'itr',
    'sanction_letter',
    'asset_leasing',
    'other'
  ];
  
  return (
    <Card className="shadow-md transition-all hover:shadow-lg border-dataroom-primary/20 bg-gradient-to-br from-white to-dataroom-primary-lighter">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium text-gray-800">{label}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {(types.length > 1 || !allowedTypes) && (
          <div className="space-y-2">
            <Label htmlFor="document-type" className="text-sm font-medium">Document Type</Label>
            <Select
              value={selectedType}
              onValueChange={(value) => setSelectedType(value as DocumentType)}
            >
              <SelectTrigger className="w-full bg-white border-dataroom-primary/30 focus:border-dataroom-primary">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-dataroom-primary/30">
                {types.map((type) => (
                  <SelectItem key={type} value={type}>
                    {formatDocumentType(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragActive 
              ? 'border-dataroom-primary bg-dataroom-primary-light' 
              : 'border-dataroom-primary/40 hover:bg-dataroom-primary-lighter'
          }`}
          onClick={handleUploadClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center space-y-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              dragActive ? 'bg-dataroom-primary-light' : 'bg-dataroom-primary-lighter'
            }`}>
              <Upload className={`h-6 w-6 ${dragActive ? 'text-dataroom-primary' : 'text-gray-500'}`} />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-medium">
                Drag files here or <span className="text-dataroom-primary">browse</span>
              </h4>
              <p className="text-xs text-gray-500">
                Supports PNG, JPEG, PDF, XLS files up to 10MB
              </p>
            </div>
          </div>
          <Input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
          />
        </div>
        
        {uploading && (
          <div className="space-y-1">
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            {/* <Progress value={uploadProgress} className="h-1.5" /> */}
          </div>
        )}

{uploading && (
  <div className="w-full h-2 bg-gray-200 rounded">
    <div
      className="h-2 bg-dataroom-primary rounded transition-all duration-100"
      style={{ width: `${ uploadProgress}%` }}
    ></div>
  </div>
)}
        
        {selectedFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Selected Files</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFiles([])}
                className="h-8 px-2 text-xs hover:bg-dataroom-primary-light"
              >
                Clear All
              </Button>
            </div>
            
            <div className="max-h-40 overflow-y-auto border rounded-md border-dataroom-primary/20">
              <ul className="divide-y divide-dataroom-primary/10">
                {selectedFiles.map((file, index) => (
                  <motion.li
                    key={`${file.name}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center justify-between p-2 text-sm hover:bg-dataroom-primary-lighter"
                  >
                    <div className="flex items-center space-x-2 truncate mr-2">
                      <FileText className="h-4 w-4 text-dataroom-primary flex-shrink-0" />
                      <span className="truncate max-w-[150px]">{file.name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="text-xs border-dataroom-primary/30">
                        {file.size < 1024 * 1024
                          ? `${(file.size / 1024).toFixed(0)} KB`
                          : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-dataroom-primary-light"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        <Button 
          onClick={handleSubmit}
          className="w-full bg-dataroom-primary hover:bg-dataroom-primary/90 text-white" 
          disabled={selectedFiles.length === 0 || uploading}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length} Files)` : ''}`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DocumentUploader;
